import React from 'react';
import { useNode } from '@craftjs/core';
import { isBlankHtml } from '@/utils/functions';
import { trackConversionFromHref } from '@/lib/track-site-event';
import { htmlSeguro } from '../utils/htmlSeguro';

type TamanoBoton = 'sm' | 'md' | 'lg';

interface WhatsAppButtonProps {
  text?: string;
  phone?: string;
  message?: string;
  bgColor?: string;
  textColor?: string;
  size?: TamanoBoton;
  borderRadius?: number;
  fullWidth?: boolean;
  align?: 'left' | 'center' | 'right';
  showIcon?: boolean;
  margin?: number;
}

export const MEDIDAS_WHATSAPP: Record<TamanoBoton, { clase: string; icono: number }> = {
  sm: { clase: 'px-4 py-2 text-sm', icono: 16 },
  md: { clase: 'px-6 py-3 text-base', icono: 20 },
  lg: { clase: 'px-8 py-4 text-lg', icono: 24 },
};

/**
 * URL de WhatsApp para el botón en línea.
 *
 * A propósito NO usa `normalizeChilePhone` (`utils/contact-utils`): ese helper
 * solo entiende móviles chilenos y devuelve `null` para cualquier otro país, lo
 * que dejaría sin botón a los tenants de fuera de Chile. Acá el número se toma
 * tal como lo escribió el dealer, con código de país, y solo se le sacan los
 * caracteres que no son dígitos.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
export const urlDeWhatsApp = (telefono: string, mensaje?: string) => {
  const digitos = (telefono || '').replace(/\D/g, '');
  if (digitos.length < 8) return null;
  const texto = (mensaje || '').trim();
  return `https://wa.me/${digitos}${texto ? `?text=${encodeURIComponent(texto)}` : ''}`;
};

/** Glifo de WhatsApp dibujado a mano: evita depender de otra librería de íconos. */
export const IconoWhatsApp = ({ size, color }: { size: number; color: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden style={{ flexShrink: 0 }}>
    <path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.39a9.86 9.86 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.44 9.9-9.9 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.02h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.03-.2-.31a8.19 8.19 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.21-8.24 8.21Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.13-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.16 0-.43.06-.65.31-.22.25-.85.83-.85 2.03s.87 2.35.99 2.51c.12.17 1.72 2.62 4.16 3.67.58.25 1.03.4 1.39.51.58.19 1.11.16 1.53.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
  </svg>
);

/**
 * Botón de WhatsApp en línea — render del sitio público.
 *
 * No tiene nada que ver con `FloatingWhatsApp`, que es el botón fijo de la
 * esquina, global a todas las páginas y con su propia configuración. Éste es un
 * elemento más: se suelta dentro de una `Section` y queda en el flujo.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/WhatsAppButton.tsx.
 */
export const WhatsAppButton = ({
  text = '',
  phone = '',
  message = '',
  bgColor = '#25D366',
  textColor = '#ffffff',
  size = 'md',
  borderRadius = 8,
  fullWidth = false,
  align = 'left',
  showIcon = true,
  margin = 0,
}: WhatsAppButtonProps) => {
  const { connectors } = useNode();

  const medidas = MEDIDAS_WHATSAPP[size] || MEDIDAS_WHATSAPP.md;
  const url = urlDeWhatsApp(phone, message);

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      // `w-full h-full` como CLASES y no medidas en el `style`, igual que
      // `Button`: un estilo en línea le gana a las clases de medida del modo
      // libre, así que el tamaño elegido no llegaba nunca.
      className="w-full h-full"
      style={{
        margin: `${margin}px 0`,
        display: 'flex',
        // Columna + `alignItems`, no fila + `justifyContent`: es la misma caja
        // pero coincide exactamente con la del editor, que abajo del botón puede
        // mostrar un aviso cuando falta el número.
        flexDirection: 'column',
        alignItems: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
      }}
    >
      {!!url && !isBlankHtml(text) && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackConversionFromHref(url)}
          className={`inline-flex items-center justify-center gap-2 font-semibold transition-opacity hover:opacity-90 ${medidas.clase}`}
          style={{
            background: bgColor,
            color: textColor,
            borderRadius: `${borderRadius}px`,
            width: fullWidth ? '100%' : 'auto',
            // El contenido se acomoda DENTRO de la caja: un `inline-flex` se
            // planta en su ancho natural y salía por el costado del frame.
            maxWidth: '100%',
          }}
        >
          {showIcon && <IconoWhatsApp size={medidas.icono} color={textColor} />}
          <span dangerouslySetInnerHTML={htmlSeguro(text || '')} />
        </a>
      )}
    </div>
  );
};

WhatsAppButton.craft = {
  displayName: 'WhatsAppButton',
  props: {
    text: 'Escribinos por WhatsApp',
    phone: '',
    message: 'Hola, me interesa saber más sobre sus vehículos.',
    bgColor: '#25D366',
    textColor: '#ffffff',
    size: 'md',
    borderRadius: 8,
    fullWidth: false,
    align: 'left',
    showIcon: true,
    margin: 0,
  },
};
